import "@mdxeditor/editor/style.css";

import {
  BoldItalicUnderlineToggles,
  headingsPlugin,
  linkPlugin,
  listsPlugin,
  ListsToggle,
  markdownShortcutPlugin,
  MDXEditor,
  MDXEditorMethods,
  quotePlugin,
  tablePlugin,
  thematicBreakPlugin,
  toolbarPlugin,
  UndoRedo,
} from "@mdxeditor/editor";
import { Box, FormControl, FormHelperText, SxProps } from "@mui/material";
import { useEffect, useRef } from "react";
import { Controller, get, useFormState, useWatch } from "react-hook-form";

import {
  AdditionalFormFieldProps,
  BaseFormFieldDefinition,
  FormFieldValidationDefinition,
} from "../form-field";

export const RICH_TEXT_FORM_FIELD_TYPE = "rich-text" as const;

const ToolbarContents = () => (
  <>
    <UndoRedo />
    <BoldItalicUnderlineToggles />
    <ListsToggle />
  </>
);

export type RichTextFormFieldDefinition = BaseFormFieldDefinition<
  typeof RICH_TEXT_FORM_FIELD_TYPE
> & {
  type: typeof RICH_TEXT_FORM_FIELD_TYPE;
  maxLength?: FormFieldValidationDefinition<number>;
  minLength?: FormFieldValidationDefinition<number>;
  multiline?: boolean;
  pattern?: FormFieldValidationDefinition<RegExp>;
  sx?: SxProps;
  showCharacterCount?: boolean;
};

function getRichTextCharacterCount(value: string): number {
  const textarea = document.createElement("textarea");
  textarea.innerHTML = value;
  return Array.from(textarea.value).length;
}

function truncateRichText(value: string, maxLength: number): string {
  const tokens =
    value.match(
      /&(?:#(?:x[\da-f]+|\d+)|[a-z][\da-z]*);|[\uD800-\uDBFF][\uDC00-\uDFFF]|[\s\S]/gi
    ) ?? [];
  const textarea = document.createElement("textarea");
  let result = "";
  let count = 0;

  for (const token of tokens) {
    let tokenCount = Array.from(token).length;
    if (token.startsWith("&") && token.endsWith(";")) {
      textarea.innerHTML = token;
      tokenCount = Array.from(textarea.value).length;
    }
    if (count + tokenCount > maxLength) {
      break;
    }
    result += token;
    count += tokenCount;
  }

  return result;
}

export function RichTextFormField({
  disabled = false,
  errorMessage,
  maxLength,
  minLength,
  name,
  pattern,
  required,
  showCharacterCount,
}: RichTextFormFieldDefinition & AdditionalFormFieldProps) {
  const { errors } = useFormState();

  const error = get(errors, name);
  const mdxEditorRef = useRef<MDXEditorMethods>(null);

  // TODO: When opening an initiative after editing a different one, the markdown doesnt get set to the new initiaties' markdown
  // Maybe a better solution?
  const value: string = useWatch({ name });
  useEffect(
    () => mdxEditorRef?.current?.setMarkdown(value ?? ""),
    [value, mdxEditorRef]
  );

  return (
    <FormControl sx={{ height: "100%" }} fullWidth>
      <Controller
        name={name}
        disabled={disabled}
        rules={{
          minLength,
          pattern,
          required,
          validate: maxLength
            ? (value: string | undefined) =>
                getRichTextCharacterCount(value ?? "") <= maxLength.value ||
                maxLength.message
            : undefined,
        }}
        render={({ field }) => (
          <Box
            onBlurCapture={field.onBlur}
            sx={(theme) => ({
              padding: "5px",
              border: `1px solid ${
                error ? theme.palette.error.main : "#0000003b"
              }`,

              borderRadius: "5px",
              backgroundColor: "#fff",
              '& [class*="toolbarRoot"]': {
                position: "static !important",
                top: "auto !important",
                backgroundColor: "white",
                zIndex: "auto",
              },
            })}
          >
            <MDXEditor
              ref={mdxEditorRef}
              readOnly={disabled}
              markdown={field.value ?? ""}
              onChange={(markdown) => {
                if (!maxLength) {
                  field.onChange(markdown);
                  return;
                }

                const limitedMarkdown = truncateRichText(
                  markdown,
                  maxLength.value
                );
                if (limitedMarkdown !== markdown) {
                  mdxEditorRef.current?.setMarkdown(limitedMarkdown);
                }
                field.onChange(limitedMarkdown);
              }}
              plugins={[
                toolbarPlugin({
                  toolbarContents: ToolbarContents,
                }),
                headingsPlugin(),
                listsPlugin(),
                quotePlugin(),
                thematicBreakPlugin(),
                linkPlugin(),
                tablePlugin(),
                markdownShortcutPlugin(),
              ]}
            />
            {error && (
              <FormHelperText
                sx={(theme) => ({ color: theme.palette.error.main })}
              >
                {errorMessage}
              </FormHelperText>
            )}
            {showCharacterCount && maxLength && (
              <FormHelperText aria-live="polite" sx={{ textAlign: "right" }}>
                {getRichTextCharacterCount(value ?? "")} / {maxLength.value}{" "}
                characters
              </FormHelperText>
            )}
          </Box>
        )}
      />
    </FormControl>
  );
}
